import React, { useEffect, useState, forwardRef, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Virtuoso } from "react-virtuoso";

import { makeStyles } from "tss-react/mui";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";

import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";

import PreviewTable from "./PreviewTable";
import { KMKDialog, ConfirmPopup } from "../../Common";
import { setPrevState } from "../../../util/common";
import { regexSnowflakeField } from "../../../util/regularExpress";
import { checkReservedKeywords } from "../../../util/restrictedStatement";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: "24px 24px 24px 24px",
    backgroundColor: "#FFFFFF",
  },
  bodyContainer: {
    width: "100%",
    height: "100%",
    marginLeft: "0",
    marginBottom: "0",
    display: "flex",
    flexDirection: "column",
  },
  Toolbar: {
    backgroundColor: "white",
    color: "black",
    padding: 0,
  },
  PanelContainer: {
    padding: "0",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
    "& .MuiTableContainer-root": {
      borderRadius: "4px",
      "&::-webkit-scrollbar-track": {
        marginTop: "36px",
      },
    },
    "&.MuiGrid-item": {
      paddingTop: 0,
    },
  },
  Panel: {
    borderRadius: "4px",
    marginBottom: 0,
  },
  mappingContainer: {
    overflow: "auto",
    marginTop: "16px",
    height: "calc(100% - 62px)",
  },
  mappingHeader: {
    fontWeight: "700",
    width: "max(24.25%, 200px)",
    display: "flex",
    justifyContent: "center",
  },
  mappingField: {
    "& legend": { display: "none" },
    "& fieldset": { top: 0 },
  },
  dialogContent: {
    padding: "0",
    overflow: "hidden",
  },
}));

const Mapping = (props) => {
  const {
    handleClose,
    validMapping,
    dataTypes,
    updateMapping,
    loading,
    append,
    options,
    columns,
    setColumns,
    btnGroup,
    formats,
    scrollToIndex,
    destinationType = "snowflake",
  } = props;

  const { classes } = useStyles();
  const previewCount = useSelector((state) => state.config.previewCount);

  const [filters, setFilters] = useState({
    search: "",
    dataType: "",
  });
  const previewTableRef = useRef(null);
  const filterRef = useRef(null);
  const virtuosoRef = useRef(null);

  const [bulkChange, setBulkChange] = useState({
    open: false,
    source: "",
    value: {},
  });

  const handleSelectAllChange = (event) => {
    const isChecked = event.target.checked;
    setColumns((prev) => ({
      ...prev,
      mappings: prev.mappings.map((mapping, i) => {
        if ($mappings.includes(mapping)) return { ...mapping, isSelected: isChecked };
        return mapping;
      }),
    }));
  };

  const handleBulkChange = (changeAll) => {
    if (changeAll) {
      const value = bulkChange.value;
      const formatValue = formats[value.type]
        ? formats[value.type].find((v) => v.value === value.format)?.value ??
          formats[value.type][formats[value.type].length - 1]?.value
        : "";
      setColumns((prev) => ({
        ...prev,
        mappings: prev.mappings.map((mapping) => {
          if (mapping.isSelected && $mappings.includes(mapping))
            return {
              ...mapping,
              ...value,
              size: options[value.type]
                ? options[value.type].find((v) => v.value === value.size)?.value ??
                  options[value.type][
                    value.type === "Number" ? 0 : value.type === "String" ? 5 : options[value.type].length - 1
                  ]?.value
                : "",
              format: formatValue,
              customFormat:
                formatValue === "Custom" ? value.customFormat : value.type === mapping.type ? mapping.customFormat : "",
            };
          else return { ...mapping, isSelected: false };
        }),
      }));
    } else updateMapping(bulkChange.value, bulkChange.source);

    setPrevState(setBulkChange, { open: false, value: {}, source: "" });
  };

  // Filtering Columns everytime there is a change in vlaue of seach or data type selection
  const $mappings = useMemo(() => {
    let filteredMappingsCols = [];

    if (filters.search.trim() === "" && filters.dataType === "") {
      return columns.mappings;
    }

    if (filters.search.trim() === "") {
      filteredMappingsCols = columns.mappings.filter(({ type }) => {
        return type === filters.dataType;
      });
    } else if (filters.dataType === "") {
      filteredMappingsCols = columns.mappings.filter(({ source }) =>
        source.toLowerCase().includes(filters.search.toLowerCase())
      );
    } else {
      filteredMappingsCols = columns.mappings.filter(
        ({ source, type }) => source.toLowerCase().includes(filters.search.toLowerCase()) && type === filters.dataType
      );
    }

    return filteredMappingsCols;
  }, [filters.dataType, filters.search, columns.mappings]);

  // checkbox for bulk change
  const selectAll = useMemo(() => ($mappings.length ? $mappings.every((item) => item.isSelected) : false), [$mappings]);
  const intermediate = useMemo(() => !selectAll && $mappings.some((item) => item.isSelected), [selectAll, $mappings]);

  const isBulkChangeRequired = (source, key, value) => {
    if ((selectAll || intermediate) && $mappings?.find((item) => item.source === source)?.isSelected) {
      if (!$mappings?.filter(({ isSelected }) => isSelected)?.every((item) => item[key] === value)) return true;
    }
    return false;
  };

  // This data will be passed to TablePreview component to render it only once
  const $dataToPreview = useMemo(() => {
    return { headers: columns.headers, data: columns.data, fileName: columns.fileName, error: columns.error };
  }, [columns.headers, columns.data, columns.fileName, columns.error]);

  useEffect(() => {
    if (scrollToIndex !== null) {
      setTimeout(() => {
        virtuosoRef.current.scrollToIndex({ index: scrollToIndex, align: "start", behavior: "smooth" });
      }, 0);
    }
  }, [scrollToIndex]);

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || !validMapping}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog
        TransitionComponent={Transition}
        open={validMapping}
        sx={{
          "& .MuiDialog-paperFullScreen": {
            maxHeight: "97%",
            width: "97%",
          },
        }}
        fullScreen
      >
        <DialogTitle sx={{ padding: "0" }}>
          <AppBar sx={{ position: "relative" }}>
            <Toolbar className={classes.Toolbar}>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                Sample Data and Mapping
              </Typography>
              {btnGroup}
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleClose}
                style={{ marginRight: 15 }}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box sx={{ width: "100%", height: "100%" }} className={classes.container}>
            <Box sx={{ mb: 2 }} className={classes.bodyContainer}>
              <Box className={classes.PanelContainer} sx={{ maxHeight: "45%" }} ref={previewTableRef}>
                <PreviewTable columns={$dataToPreview} />
              </Box>
              <Box
                className={classes.PanelContainer}
                sx={{
                  maxHeight: `calc(100% - ${previewTableRef?.current?.clientHeight || 0}px)`,
                  flex: 1,
                  overflowY: "clip",
                  marginTop: "10px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems={"baseline"}
                  sx={{ marginBottom: "10px" }}
                  ref={filterRef}
                >
                  <span
                    style={{
                      color: "#ed6c02",
                      padding: "0",
                      margin: "0",
                      marginRight: "20px",
                      marginTop: "15px",
                    }}
                  >
                    Mapping
                  </span>
                  <TextField
                    margin="none"
                    size="small"
                    label="Search Attribute"
                    value={filters.search}
                    onChange={(e) => {
                      setColumns((prev) => ({
                        ...prev,
                        mappings: prev.mappings.map((mapping, i) => {
                          return {
                            ...mapping,
                            isSelected: mapping.source.toLowerCase().includes(e.target.value.toLowerCase())
                              ? mapping.isSelected
                              : false,
                          };
                        }),
                      }));
                      setPrevState(setFilters, { search: e.target.value });
                    }}
                    sx={{
                      width: "280px",
                      mr: "20px",
                      mt: "15px",
                    }}
                    InputProps={
                      filters.search !== ""
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setPrevState(setFilters, { search: "" })} edge="end">
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : {}
                    }
                  />
                  <TextField
                    sx={{ my: 0, width: "280px", mt: "15px" }}
                    select
                    label="Data Type"
                    size="small"
                    margin="none"
                    value={filters.dataType}
                    helperText={`Type detection based on first ${previewCount} rows`}
                    InputProps={
                      filters.dataType !== ""
                        ? {
                            endAdornment: (
                              <InputAdornment
                                position="start"
                                sx={{
                                  mr: "20px",
                                }}
                              >
                                <IconButton onClick={() => setPrevState(setFilters, { dataType: "" })} edge="end">
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : {}
                    }
                    onChange={(e) => {
                      setColumns((prev) => ({
                        ...prev,
                        mappings: prev.mappings.map((mapping, i) => {
                          return {
                            ...mapping,
                            isSelected: mapping.type === e.target.value ? mapping.isSelected : false,
                          };
                        }),
                      }));
                      setPrevState(setFilters, { dataType: e.target.value });
                    }}
                  >
                    {dataTypes.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Paper
                  elevation={3}
                  className={classes.Panel}
                  direction="column"
                  sx={{
                    marginTop: "10px",
                    overflow: "hidden",
                    maxHeight: "calc(100% - 100px)",
                    minHeight: "calc(100% - 100px)",
                  }}
                >
                  <Box
                    sx={{
                      overflowX: "auto",
                      width: "max(100%, 900px)",
                      height: "100%",
                    }}
                    p={0}
                    m={0}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "white",
                        zIndex: 1000,
                        position: "sticky",
                        top: 0,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          width: "max(3%, 80px)",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        <Checkbox
                          checked={selectAll}
                          indeterminate={intermediate}
                          onChange={(e) => handleSelectAllChange(e)}
                          disabled={$mappings.length === 0}
                          sx={{ ml: "6px" }}
                        />
                        <Typography
                          sx={{
                            position: "absolute",
                            left: "50px",
                            top: { xs: "0", md: "10px" },
                            width: { xs: "60px", md: "80px" },
                            fontWeight: "700",
                            wordWrap: "break-word",
                          }}
                        >
                          Select All
                        </Typography>
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Source Column
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Destination Column
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Data Type
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Length/Format
                      </Box>
                    </Box>
                    {$mappings.length !== 0 ? (
                      <Box className={classes.mappingContainer}>
                        <Virtuoso
                          totalCount={$mappings.length}
                          data={$mappings}
                          ref={virtuosoRef}
                          style={{ height: "100%", width: "100%" }}
                          itemContent={(
                            _index,
                            {
                              source,
                              destination,
                              type,
                              size,
                              format,
                              customFormat,
                              hasError,
                              errorMessage,
                              isSelected,
                            }
                          ) => (
                            <Box sx={{ m: 0, p: 0, display: "flex", alignItems: "flex-start" }} key={source}>
                              <Box sx={{ display: "flex", width: "max(3%, 80px)" }}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => updateMapping({ isSelected: e.target.checked }, source)}
                                  sx={{ m: "10px 6px" }}
                                />
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <TextField
                                  margin="none"
                                  size="small"
                                  fullWidth
                                  placeholder="Source Column"
                                  value={source}
                                  inputProps={{ readOnly: true }}
                                  className={classes.mappingField}
                                />
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <TextField
                                  margin="none"
                                  fullWidth
                                  placeholder="Destination Column"
                                  value={destination}
                                  size="small"
                                  dataid={destination}
                                  error={hasError}
                                  helperText={hasError ? errorMessage : ""}
                                  onBlur={(e) => {
                                    let hasError = false;
                                    let errorMessage = "";
                                    if (checkReservedKeywords(e.target.value, destinationType)) {
                                      hasError = true;
                                      errorMessage = "Reserved keyword";
                                    } else if (e.target.value.length > 128) {
                                      hasError = true;
                                      errorMessage = "Max limit: 128 Characters!";
                                    }

                                    updateMapping({ hasError, errorMessage }, source);
                                  }}
                                  onInput={(e) => {
                                    if (e.target.value !== "" && !regexSnowflakeField(e.target.value)) {
                                      e.returnValue = false;
                                      e.preventDefault();
                                      return;
                                    }
                                    updateMapping({ destination: e.target.value }, source);
                                  }}
                                  inputProps={{ maxLength: 128, ...(append ? { readOnly: true } : {}) }}
                                  className={classes.mappingField}
                                  style={{ scrollMarginBlockStart: "20px" }}
                                />
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <FormControl size="small" fullWidth margin="none" sx={{ my: 0 }}>
                                  {append ? (
                                    <TextField
                                      margin="none"
                                      fullWidth
                                      value={type}
                                      size="small"
                                      inputProps={{ readOnly: true }}
                                      className={classes.mappingField}
                                    />
                                  ) : (
                                    <Select
                                      value={dataTypes.length ? type : ""}
                                      onChange={(e) => {
                                        if (isBulkChangeRequired(source, "type", e.target.value))
                                          setBulkChange({
                                            open: true,
                                            value: { type: e.target.value, size, format, customFormat },
                                            source,
                                          });
                                        else updateMapping({ type: e.target.value }, source);
                                      }}
                                      className={classes.mappingField}
                                    >
                                      {dataTypes.map(({ text, value }) => (
                                        <MenuItem key={value} value={value}>
                                          {text}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </FormControl>
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <FormControl size="small" fullWidth margin="none" sx={{ my: 0 }}>
                                  <>
                                    {options[type] && append ? (
                                      <Select value={size || ""} className={classes.mappingField} disabled={append}>
                                        <MenuItem key={(size || "").toString()} value={size || ""}>
                                          {size || ""}
                                        </MenuItem>
                                      </Select>
                                    ) : options[type] ? (
                                      <Select
                                        value={options[type] ? size : ""}
                                        onChange={(e) => {
                                          if (isBulkChangeRequired(source, "size", e.target.value))
                                            setBulkChange({
                                              open: true,
                                              value: { type, size: e.target.value, format, customFormat },
                                              source,
                                            });
                                          else updateMapping({ size: e.target.value }, source);
                                        }}
                                        className={classes.mappingField}
                                        disabled={append}
                                      >
                                        {options[type].map(({ value }) => (
                                          <MenuItem key={value.toString()} value={value}>
                                            {value}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    ) : formats[type] ? (
                                      <Grid container>
                                        <Grid
                                          style={{ margin: 0, padding: format === "Custom" ? "0px 4px 0px 0px" : 0 }}
                                          size={{
                                            xs: 12,
                                            sm: format === "Custom" ? 6 : 12,
                                          }}
                                        >
                                          <Select
                                            value={formats[type] ? format : ""}
                                            onChange={(e) => {
                                              if (isBulkChangeRequired(source, "format", e.target.value))
                                                setBulkChange({
                                                  open: true,
                                                  value: { type, size, format: e.target.value, customFormat },
                                                  source,
                                                });
                                              else updateMapping({ format: e.target.value }, source);
                                            }}
                                            fullWidth
                                            className={classes.mappingField}
                                          >
                                            {formats[type].map(({ value }) => (
                                              <MenuItem key={value.toString()} value={value}>
                                                {value}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </Grid>
                                        {format === "Custom" && (
                                          <Grid
                                            size={{
                                              xs: 12,
                                              sm: 6,
                                            }}
                                          >
                                            <TextField
                                              margin="none"
                                              fullWidth
                                              value={customFormat}
                                              size="small"
                                              style={{ margin: 0, padding: "0px 0px 0px 4px" }}
                                              inputProps={{ readOnly: false, maxLength: 30 }}
                                              className={classes.mappingField}
                                              onChange={(e) => updateMapping({ customFormat: e.target.value }, source)}
                                              onBlur={(e) => {
                                                if (isBulkChangeRequired(source, "customFormat", e.target.value))
                                                  setBulkChange({
                                                    open: true,
                                                    value: { type, size, format, customFormat: e.target.value },
                                                    source,
                                                  });
                                              }}
                                              required
                                            />
                                          </Grid>
                                        )}
                                      </Grid>
                                    ) : (
                                      <Select value="" aria-disabled disabled className={classes.mappingField}></Select>
                                    )}
                                  </>
                                </FormControl>
                              </Box>
                            </Box>
                          )}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignContent: "center",
                          alignItems: "center",
                          height: "90%",
                        }}
                      >
                        No records found
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </KMKDialog>
      {bulkChange.open && (
        <ConfirmPopup
          configData={{
            popupTitle: `Confirmation Required`,
            popupMessage: `Apply this changes to all the selected items?`,
            handleCancel: () => handleBulkChange(false),
            handleClick: () => handleBulkChange(true),
            loadingForConfirm: loading,
          }}
        />
      )}
    </>
  );
};

export default Mapping;
